import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronUp, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

const FloatingButtons = ({ audioRef, opened}) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.play();
        setIsMuted(false);
      } else {
        audioRef.current.pause();
        setIsMuted(true);
      }
    }
  };

  if (!opened) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ scale: 0, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 20 }}
            whileHover={{ scale: 1.15, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToTop}
            className="relative w-14 h-14 rounded-full overflow-hidden shadow-2xl group"
          >
            {/* Glassy background */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-lg"></div>

            {/* Border glow */}
            <div className="absolute inset-0 border-2 border-white/40 rounded-full"></div>

            {/* Icon */}
            <div className="relative w-full h-full flex items-center justify-center">
              <FaChevronUp className="text-sage text-xl group-hover:text-sage-dark transition-colors drop-shadow-lg" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Music Toggle Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        whileHover={{ scale: 1.15, y: -2 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleMusic}
        className="relative w-14 h-14 rounded-full overflow-hidden shadow-2xl group"
      >
        {/* Glassy background with color based on state */}
        <div className={`absolute inset-0 backdrop-blur-lg transition-all ${
          isMuted
            ? 'bg-gradient-to-br from-red-300/30 to-red-100/10'
            : 'bg-gradient-to-br from-green-300/30 to-green-100/10'
        }`}></div>

        {/* Border glow */}
        <div className="absolute inset-0 border-2 border-white/40 rounded-full"></div>

        {/* Icon with animation */}
        <div className="relative w-full h-full flex items-center justify-center">
          <AnimatePresence mode="wait">
            {isMuted ? (
              <motion.div
                key="muted"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                <FaVolumeMute className="text-red-600 text-xl drop-shadow-lg" />
              </motion.div>
            ) : (
              <motion.div
                key="unmuted"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                <FaVolumeUp className="text-green-600 text-xl drop-shadow-lg" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>
    </div>
  );
};

export default FloatingButtons;
